"use client";

import { useEffect, useRef, useState, useTransition } from "react";
import { posthog } from "@/lib/analytics/posthog";
import type { DailyLessonView } from "@/lib/learning/resolve-user-track";
import { loadAnotherLesson } from "@/server/actions/lesson";

/**
 * The daily micro-lesson card on /seeker — the retention habit surface.
 *
 * Compact by default: only the title + actionable tip show, with the longer
 * body behind "Read more" (the body was always-expanded and ate vertical space
 * above the chat). "Show another lesson" cycles back through the track's
 * curriculum (seq history) so a returning user isn't stuck on the same content
 * every day — the prior behavior, since the daily cron emits one lesson per
 * track per day and the card always showed the latest.
 *
 * Client-side because both interactions are stateful; the streak is still
 * bumped server-side on the initial daily view (getDailyLessonForUser).
 */
export function DailyLessonCard({
	lesson,
	maxSeq,
	locale,
}: {
	lesson: DailyLessonView;
	/** Highest curriculum seq for this track (the daily lesson's own seq). */
	maxSeq: number;
	locale: "en" | "id";
}) {
	const isEn = locale === "en";
	const [view, setView] = useState({
		title: lesson.title,
		bodyMd: lesson.bodyMd,
		tip: lesson.tip,
		sourceUrl: lesson.sourceUrl,
		seq: lesson.seq,
		kind: lesson.kind,
	});
	const isQuiz = view.kind === "quiz";
	const [expanded, setExpanded] = useState(false);
	const [pending, startTransition] = useTransition();
	const viewedRef = useRef(false);

	// Fire the daily view once on mount (the streak signal is server-recorded;
	// this is the analytics counterpart that was previously missing).
	useEffect(() => {
		if (viewedRef.current) return;
		viewedRef.current = true;
		posthog?.capture("daily_lesson_viewed", {
			track: lesson.track,
			title: lesson.title,
			streak: lesson.streak,
			seq: lesson.seq,
			locale,
		});
	}, [lesson.track, lesson.title, lesson.streak, lesson.seq, locale]);

	// Only offer recycling when there's more than one lesson to browse.
	const canRecycle = maxSeq > 0;
	const isLatest = view.seq === maxSeq;

	function showAnother() {
		if (pending || !canRecycle) return;
		// Step back through history; wrap to the latest at the start.
		const nextSeq = view.seq > 0 ? view.seq - 1 : maxSeq;
		const fromSeq = view.seq;
		startTransition(async () => {
			const next = await loadAnotherLesson(locale, nextSeq);
			if (!next) return;
			setExpanded(false);
			setView({
				title: next.title,
				bodyMd: next.bodyMd,
				tip: next.tip,
				sourceUrl: next.sourceUrl,
				seq: next.seq,
				kind: next.kind,
			});
			posthog?.capture("daily_lesson_recycled", {
				track: lesson.track,
				from_seq: fromSeq,
				to_seq: next.seq,
				locale,
			});
		});
	}

	function backToToday() {
		if (pending || isLatest) return;
		startTransition(async () => {
			const latest = await loadAnotherLesson(locale, maxSeq);
			if (!latest) return;
			setExpanded(false);
			setView({
				title: latest.title,
				bodyMd: latest.bodyMd,
				tip: latest.tip,
				sourceUrl: latest.sourceUrl,
				seq: latest.seq,
				kind: latest.kind,
			});
		});
	}

	function toggleExpand() {
		const next = !expanded;
		setExpanded(next);
		if (next) {
			posthog?.capture("daily_lesson_expanded", {
				track: lesson.track,
				seq: view.seq,
				locale,
			});
			if (isQuiz) {
				posthog?.capture("daily_quiz_answer_revealed", {
					track: lesson.track,
					seq: view.seq,
					locale,
				});
			}
		}
	}

	return (
		<section className="rounded-xl border border-violet-300/40 bg-violet-50/50 p-4 dark:border-violet-800/40 dark:bg-violet-950/20">
			<div className="mb-2 flex items-center justify-between gap-2">
				<p className="text-xs font-semibold uppercase tracking-widest text-violet-700 dark:text-violet-300">
					{isQuiz
						? isEn
							? "Daily quiz"
							: "Kuis harian"
						: isEn
							? "Daily lesson"
							: "Pelajaran harian"}{" "}
					· {lesson.trackLabel}
				</p>
				{lesson.streak > 1 ? (
					<span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700 dark:bg-amber-950 dark:text-amber-400">
						{lesson.streak} {isEn ? "day streak" : "hari berturut"}
					</span>
				) : null}
			</div>

			<h2 className="text-sm font-bold text-foreground">{view.title}</h2>

			{isQuiz ? (
				<>
					<p className="mt-1 whitespace-pre-line text-sm leading-relaxed text-foreground">
						{view.bodyMd}
					</p>
					{expanded ? (
						<p className="mt-2 text-sm font-medium text-foreground">
							{view.tip}
						</p>
					) : null}
				</>
			) : (
				<>
					{expanded ? (
						<p className="mt-1 whitespace-pre-line text-sm leading-relaxed text-muted-foreground">
							{view.bodyMd}
						</p>
					) : null}
					<p className="mt-2 text-sm font-medium text-foreground">{view.tip}</p>
				</>
			)}

			<div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs">
				<button
					type="button"
					onClick={toggleExpand}
					className="cursor-pointer font-medium text-violet-700 hover:text-violet-900 dark:text-violet-400"
				>
					{isQuiz
						? expanded
							? isEn
								? "Hide answer"
								: "Sembunyikan jawaban"
							: isEn
								? "Reveal answer"
								: "Lihat jawaban"
						: expanded
							? isEn
								? "Show less"
								: "Tutup"
							: isEn
								? "Read more"
								: "Selengkapnya"}
				</button>

				{view.sourceUrl ? (
					<a
						href={view.sourceUrl}
						target="_blank"
						rel="nofollow noopener noreferrer"
						onClick={() =>
							posthog?.capture("daily_lesson_source_clicked", {
								track: lesson.track,
								seq: view.seq,
								locale,
							})
						}
						className="text-violet-700 underline hover:text-violet-900 dark:text-violet-400"
					>
						{isEn ? "Source" : "Sumber"} ↗
					</a>
				) : null}

				{canRecycle ? (
					<button
						type="button"
						onClick={showAnother}
						disabled={pending}
						className="ml-auto cursor-pointer font-medium text-muted-foreground hover:text-foreground disabled:opacity-50"
					>
						{isEn ? "Show another →" : "Lainnya →"}
					</button>
				) : null}

				{!isLatest ? (
					<button
						type="button"
						onClick={backToToday}
						disabled={pending}
						className="cursor-pointer font-medium text-muted-foreground hover:text-foreground disabled:opacity-50"
					>
						{isEn ? "Back to today" : "Hari ini"}
					</button>
				) : null}
			</div>
		</section>
	);
}
